import type {
  DimensionType,
  MaterialCondition,
  DepthType,
  ControlFrameCharacteristic,
  Orientation,
  EnclosureShape,
  Handedness,
  ThreadSystem,
} from '@frontend/api';
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  Select,
} from '@frontend/components';

import {
  DIMENSION_TYPE_OPTIONS,
  MATERIAL_CONDITION_OPTIONS,
  DEPTH_TYPE_OPTIONS,
  CHARACTERISTIC_OPTIONS,
  ORIENTATION_OPTIONS,
  ENCLOSURE_SHAPE_OPTIONS,
  HANDEDNESS_OPTIONS,
  THREAD_SYSTEM_OPTIONS,
} from './constants';

// Base props for all typed selects
type BaseSelectProps = {
  name: string;
  label: string;
  error?: string;
  touched?: boolean;
  isRequired?: boolean;
};

// Dimension Type Select
type DimensionTypeSelectProps = BaseSelectProps & {
  value: DimensionType;
  onChange: (value: DimensionType) => void;
};

export const DimensionTypeSelect = ({
  name,
  label,
  value,
  onChange,
  error,
  touched,
}: DimensionTypeSelectProps) => {
  const selectedOption = DIMENSION_TYPE_OPTIONS.find(
    (option) => option.value === value,
  );

  return (
    <FormControl>
      <FormLabel>{label}</FormLabel>
      <Select
        name={name}
        options={DIMENSION_TYPE_OPTIONS}
        value={selectedOption}
        onChange={(
          selectedOption: { value: DimensionType; label: string } | null,
        ) => {
          if (selectedOption?.value) {
            onChange(selectedOption.value);
          }
        }}
        size="sm"
        isClearable={false}
      />
      {error && touched && <FormErrorMessage>{error}</FormErrorMessage>}
    </FormControl>
  );
};

// Material Condition Select
type MaterialConditionSelectProps = BaseSelectProps & {
  value?: MaterialCondition;
  onChange: (value: MaterialCondition | undefined) => void;
  placeholder?: string;
};

export const MaterialConditionSelect = ({
  name,
  label,
  value,
  onChange,
  error,
  touched,
  placeholder = 'Select material condition',
}: MaterialConditionSelectProps) => {
  const selectedOption = value
    ? MATERIAL_CONDITION_OPTIONS.find((option) => option.value === value)
    : undefined;

  return (
    <FormControl>
      {label && <FormLabel>{label}</FormLabel>}
      <Select
        name={name}
        options={MATERIAL_CONDITION_OPTIONS}
        value={selectedOption}
        onChange={(
          selectedOption: { value: MaterialCondition; label: string } | null,
        ) => {
          onChange(selectedOption?.value);
        }}
        size="sm"
        placeholder={placeholder}
        isClearable={false}
      />
      {error && touched && <FormErrorMessage>{error}</FormErrorMessage>}
    </FormControl>
  );
};

// Depth Type Select
type DepthTypeSelectProps = BaseSelectProps & {
  value: DepthType;
  onChange: (value: DepthType) => void;
};

export const DepthTypeSelect = ({
  name,
  label,
  value,
  onChange,
  error,
  touched,
}: DepthTypeSelectProps) => {
  const selectedOption = DEPTH_TYPE_OPTIONS.find(
    (option) => option.value === value,
  );

  return (
    <FormControl>
      <FormLabel>{label}</FormLabel>
      <Select
        name={name}
        options={DEPTH_TYPE_OPTIONS}
        value={selectedOption}
        defaultValue={DEPTH_TYPE_OPTIONS[0]}
        onChange={(
          selectedOption: { value: DepthType; label: string } | null,
        ) => {
          if (selectedOption?.value) {
            onChange(selectedOption.value);
          }
        }}
        size="sm"
        isClearable={false}
      />
      {error && touched && <FormErrorMessage>{error}</FormErrorMessage>}
    </FormControl>
  );
};

// Control Frame Characteristic Select
type CharacteristicSelectProps = BaseSelectProps & {
  value: ControlFrameCharacteristic;
  onChange: (value: ControlFrameCharacteristic) => void;
};

export const CharacteristicSelect = ({
  name,
  label,
  value,
  onChange,
  error,
  touched,
}: CharacteristicSelectProps) => {
  const selectedOption = CHARACTERISTIC_OPTIONS.find(
    (option) => option.value === value,
  );

  return (
    <FormControl>
      <FormLabel>{label}</FormLabel>
      <Select
        name={name}
        options={CHARACTERISTIC_OPTIONS}
        value={selectedOption}
        onChange={(
          selectedOption: {
            value: ControlFrameCharacteristic;
            label: string;
          } | null,
        ) => {
          if (selectedOption?.value) {
            onChange(selectedOption.value);
          }
        }}
        size="sm"
        isClearable={false}
      />
      {error && touched && <FormErrorMessage>{error}</FormErrorMessage>}
    </FormControl>
  );
};

// Orientation Select
type OrientationSelectProps = BaseSelectProps & {
  value?: Orientation;
  onChange: (value: Orientation | undefined) => void;
};

export const OrientationSelect = ({
  name,
  label,
  value,
  onChange,
  error,
  touched,
}: OrientationSelectProps) => {
  const selectedOption = value
    ? ORIENTATION_OPTIONS.find((option) => option.value === value)
    : undefined;

  return (
    <FormControl>
      <FormLabel>{label}</FormLabel>
      <Select
        name={name}
        options={ORIENTATION_OPTIONS}
        value={selectedOption}
        onChange={(
          selectedOption: { value: Orientation; label: string } | null,
        ) => {
          onChange(selectedOption?.value);
        }}
        size="sm"
        placeholder="Select orientation"
        isClearable
      />
      {error && touched && <FormErrorMessage>{error}</FormErrorMessage>}
    </FormControl>
  );
};

// Enclosure Shape Select
type EnclosureShapeSelectProps = BaseSelectProps & {
  value?: EnclosureShape;
  onChange: (value: EnclosureShape | undefined) => void;
};

export const EnclosureShapeSelect = ({
  name,
  label,
  value,
  onChange,
  error,
  touched,
}: EnclosureShapeSelectProps) => {
  const selectedOption = value
    ? ENCLOSURE_SHAPE_OPTIONS.find((option) => option.value === value)
    : undefined;

  return (
    <FormControl>
      <FormLabel>{label}</FormLabel>
      <Select
        name={name}
        options={ENCLOSURE_SHAPE_OPTIONS}
        value={selectedOption}
        onChange={(
          selectedOption: { value: EnclosureShape; label: string } | null,
        ) => {
          onChange(selectedOption?.value);
        }}
        size="sm"
        placeholder="Select enclosure shape"
        isClearable
      />
      {error && touched && <FormErrorMessage>{error}</FormErrorMessage>}
    </FormControl>
  );
};

// Handedness Select
type HandednessSelectProps = BaseSelectProps & {
  value?: Handedness;
  onChange: (value: Handedness | undefined) => void;
};

export const HandednessSelect = ({
  name,
  label,
  value,
  onChange,
  error,
  touched,
}: HandednessSelectProps) => {
  const selectedOption = value
    ? HANDEDNESS_OPTIONS.find((option) => option.value === value)
    : undefined;

  return (
    <FormControl>
      <FormLabel>{label}</FormLabel>
      <Select
        name={name}
        options={HANDEDNESS_OPTIONS}
        value={selectedOption}
        onChange={(
          selectedOption: { value: Handedness; label: string } | null,
        ) => {
          onChange(selectedOption?.value);
        }}
        size="sm"
        placeholder="Select handedness"
        isClearable
      />
      {error && touched && <FormErrorMessage>{error}</FormErrorMessage>}
    </FormControl>
  );
};

// Thread System Select
type ThreadSystemSelectProps = BaseSelectProps & {
  value: ThreadSystem;
  onChange: (value: ThreadSystem) => void;
};

export const ThreadSystemSelect = ({
  name,
  label,
  value,
  onChange,
  error,
  touched,
}: ThreadSystemSelectProps) => {
  const selectedOption = THREAD_SYSTEM_OPTIONS.find(
    (option) => option.value === value,
  );

  return (
    <FormControl>
      <FormLabel>{label}</FormLabel>
      <Select
        name={name}
        options={THREAD_SYSTEM_OPTIONS}
        value={selectedOption}
        onChange={(
          selectedOption: { value: ThreadSystem; label: string } | null,
        ) => {
          if (selectedOption?.value) {
            onChange(selectedOption.value);
          }
        }}
        size="sm"
        isClearable={false}
      />
      {error && touched && <FormErrorMessage>{error}</FormErrorMessage>}
    </FormControl>
  );
};
